import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { dateIt } from "@/lib/format";
import { Loader2, RadioTower, Play, Clock, CheckCircle2, AlertTriangle, Power, Globe, MapPin } from "lucide-react";

function StatChip({ label, value, color }) {
  return (
    <div className="text-center px-4 py-3 border border-white/10 rounded-sm bg-[#0A1826]">
      <div className="font-mono text-2xl" style={{ color: color || "#F4F4F5" }}>{value}</div>
      <div className="text-[10px] text-zinc-500 uppercase tracking-wider mt-1">{label}</div>
    </div>
  );
}

export default function Ingestion() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const [status, setStatus] = useState(null);
  const [runs, setRuns] = useState([]);
  const [triggering, setTriggering] = useState(false);
  const [zonesInput, setZonesInput] = useState("");
  const [prefInput, setPrefInput] = useState("");
  const [prefSynced, setPrefSynced] = useState(false);

  const load = useCallback(() => {
    api.get("/ingestion/status").then((r) => setStatus(r.data)).catch(() => {});
    api.get("/ingestion/runs").then((r) => setRuns(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  // popola le zone preferite (una sola volta al primo caricamento)
  useEffect(() => {
    if (status && !prefSynced) {
      const pz = (status.config?.preferred_zones || []).join(", ");
      setPrefInput(pz);
      if (pz && !zonesInput) setZonesInput(pz);
      setPrefSynced(true);
    }
  }, [status, prefSynced, zonesInput]);

  if (!status)
    return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 text-[#EA7B2B] animate-spin" /></div>;

  const cfg = status.config;
  const running = status.running;

  const parseZones = (s) => (s || "").split(",").map((z) => z.trim()).filter(Boolean);

  const runNow = async () => {
    setTriggering(true);
    const zones = parseZones(zonesInput);
    try {
      await api.post("/ingestion/run", { zones: zones.length ? zones : null });
      toast.success(zones.length ? `Ricerca avviata sulle zone: ${zones.join(", ")}` : "Ricerca avviata: scraping + analisi AI in corso…");
      setTimeout(load, 1500);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Errore avvio ricerca");
    } finally {
      setTriggering(false);
    }
  };

  const savePreferredZones = async () => {
    await saveCfg({ preferred_zones: parseZones(prefInput) });
  };

  const saveCfg = async (patch) => {
    try {
      const { data } = await api.put("/ingestion/config", patch);
      setStatus((s) => ({ ...s, config: data }));
      toast.success("Configurazione ingestion aggiornata");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const inputCls = "bg-[#0A1826] border border-white/10 rounded-sm px-3 py-2 text-sm text-white font-mono w-24 focus:outline-none focus:ring-1 focus:ring-[#EA7B2B]";

  return (
    <div className="space-y-6" data-testid="ingestion-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-light text-white">Fonti & Ingestion</h1>
          <p className="text-sm text-zinc-500 mt-1">Monitoraggio automatico dei portali · download documenti · analisi AI delle perizie</p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 bg-[#0A1826] border border-white/10 rounded-sm px-3 min-w-[300px]">
              <MapPin className="w-4 h-4 text-[#EA7B2B]" />
              <input
                value={zonesInput}
                onChange={(e) => setZonesInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !triggering && !running) runNow(); }}
                placeholder="Comuni/province: es. Roma, Milano, TO"
                data-testid="ingestion-zones-input"
                className="bg-transparent py-2.5 text-sm text-white focus:outline-none flex-1 w-full"
              />
            </div>
            <button onClick={runNow} disabled={triggering || running} data-testid="run-ingestion-btn" className="flex items-center gap-2 bg-[#EA7B2B] text-black text-sm font-medium px-5 py-2.5 rounded-sm hover:bg-[#F59E4A] transition-colors disabled:opacity-60 whitespace-nowrap">
              {triggering || running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              {running ? "Ricerca in corso…" : "Cerca nuove operazioni"}
            </button>
          </div>
          <p className="text-[11px] text-zinc-500 max-w-[420px] text-right">Lascia vuoto per cercare ovunque. Separa più zone con la virgola. Cerca sugli annunci pubblici disponibili nelle zone indicate (best-effort).</p>
        </div>
      </div>

      {/* Compliance note */}
      <div className="text-xs text-zinc-400 bg-[#EA7B2B]/5 border border-[#EA7B2B]/20 rounded-sm px-4 py-3 flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 text-[#EA7B2B] shrink-0 mt-0.5" />
        <span>Connettore <strong className="text-zinc-200">best-effort</strong> su fonti pubbliche. Vengono acquisiti solo dati pubblicamente disponibili nel rispetto dei termini d'uso. I risultati possono essere parziali o richiedere verifica manuale (es. perizie multi-lotto). Deduplica per Tribunale + RGE + Lotto.</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Scheduler */}
        <div className="lg:col-span-2 bg-[#122A3F] border border-white/10 rounded-sm p-5" data-testid="scheduler-card">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-sm font-medium text-zinc-200 uppercase tracking-wider flex items-center gap-2"><Clock className="w-4 h-4" /> Esecuzione automatica</h2>
            <button
              onClick={() => isAdmin && saveCfg({ enabled: !cfg.enabled })}
              disabled={!isAdmin}
              data-testid="scheduler-toggle"
              className={`flex items-center gap-2 px-4 py-2 rounded-sm text-sm border transition-colors ${cfg.enabled ? "text-[#10B981] border-[#10B981]/40 bg-[#10B981]/10" : "text-zinc-400 border-white/10 bg-white/5"} ${!isAdmin ? "opacity-60 cursor-not-allowed" : ""}`}
            >
              <Power className="w-4 h-4" /> {cfg.enabled ? "Attivo" : "Spento"}
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-zinc-400">Frequenza (ore)</label>
              <div className="flex items-center gap-2 mt-1">
                <select disabled={!isAdmin} value={cfg.interval_hours} onChange={(e) => saveCfg({ interval_hours: Number(e.target.value) })} data-testid="interval-select" className={inputCls + " w-auto"}>
                  {[1, 2, 4, 6, 12, 24].map((h) => <option key={h} value={h}>ogni {h}h</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs text-zinc-400">Operazioni max per ciclo</label>
              <div className="flex items-center gap-2 mt-1">
                <input type="number" disabled={!isAdmin} value={cfg.max_per_run} onChange={(e) => saveCfg({ max_per_run: Number(e.target.value) })} data-testid="max-per-run" className={inputCls} min={1} max={30} />
              </div>
            </div>
          </div>

          <div className="mt-5 pt-4 border-t border-white/10">
            <label className="text-xs text-zinc-400 flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-[#EA7B2B]" /> Zone preferite (ricerca automatica)</label>
            <div className="flex items-center gap-2 mt-2">
              <input
                disabled={!isAdmin}
                value={prefInput}
                onChange={(e) => setPrefInput(e.target.value)}
                placeholder="es. Roma, Milano, TO — usate ad ogni ricerca schedulata"
                data-testid="preferred-zones-input"
                className="flex-1 bg-[#0A1826] border border-white/10 rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#EA7B2B] disabled:opacity-60"
              />
              {isAdmin && (
                <button onClick={savePreferredZones} data-testid="save-preferred-zones" className="text-xs bg-[#122A3F] border border-white/15 text-white px-3 py-2 rounded-sm hover:border-[#EA7B2B]/60 transition-colors whitespace-nowrap">Salva zone</button>
              )}
            </div>
            {(cfg.preferred_zones || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="preferred-zones-chips">
                {cfg.preferred_zones.map((z) => (
                  <span key={z} className="text-[11px] text-[#EA7B2B] border border-[#EA7B2B]/30 bg-[#EA7B2B]/10 px-2 py-0.5 rounded-full">{z}</span>
                ))}
              </div>
            )}
          </div>

          <div className="mt-5 pt-4 border-t border-white/10 flex items-center justify-between text-sm">
            <span className="text-zinc-500">Prossima esecuzione</span>
            <span className="font-mono text-zinc-300">{status.next_run ? new Date(status.next_run).toLocaleString("it-IT") : cfg.enabled ? "in schedulazione…" : "—"}</span>
          </div>
          {!isAdmin && <p className="text-xs text-zinc-600 mt-3">Solo l'amministratore può modificare la schedulazione.</p>}
        </div>

        {/* Source */}
        <div className="bg-[#122A3F] border border-white/10 rounded-sm p-5" data-testid="sources-card">
          <h2 className="text-sm font-medium text-zinc-200 uppercase tracking-wider flex items-center gap-2 mb-4"><Globe className="w-4 h-4" /> Fonte attiva</h2>
          <div className="space-y-2">
            {status.sources.map((s) => {
              const active = cfg.source === s.key;
              return (
                <button
                  key={s.key}
                  onClick={() => isAdmin && !active && saveCfg({ source: s.key })}
                  disabled={!isAdmin}
                  data-testid={`source-${s.key}`}
                  className={`w-full flex items-center gap-3 p-3 border rounded-sm text-left transition-colors ${active ? "border-[#EA7B2B]/40 bg-[#EA7B2B]/5" : "border-white/10 bg-white/5 hover:bg-white/10"} ${!isAdmin ? "cursor-default" : ""}`}
                >
                  <RadioTower className={`w-5 h-5 ${active ? "text-[#EA7B2B]" : "text-zinc-500"}`} />
                  <div className="flex-1">
                    <div className="text-sm text-white">{s.label}</div>
                    <div className="text-xs text-zinc-500">{s.key === "astegiudiziarie" ? "astegiudiziarie.it · dati PVP/XML" : s.key === "astalegale" ? "astalegale.net · RSS + dettaglio" : s.key}</div>
                  </div>
                  {active && <CheckCircle2 className="w-4 h-4 text-[#EA7B2B]" />}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-zinc-600 mt-3">Il cross-check multi-portale confronta automaticamente le procedure comuni tra le fonti. {isAdmin ? "Seleziona la fonte e avvia la ricerca." : "Solo l'amministratore può cambiare fonte."}</p>
        </div>
      </div>

      {/* Last run */}
      {status.last_run && (
        <div className="bg-[#122A3F] border border-white/10 rounded-sm p-5" data-testid="last-run-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-medium text-zinc-200 uppercase tracking-wider">Ultima esecuzione</h2>
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border ${status.last_run.status === "completed" ? "text-[#10B981] border-[#10B981]/30 bg-[#10B981]/10" : status.last_run.status === "running" ? "text-[#D97706] border-[#D97706]/30 bg-[#D97706]/10" : "text-[#E11D48] border-[#E11D48]/30 bg-[#E11D48]/10"}`}>{status.last_run.status}</span>
          </div>
          {(status.last_run.zones || []).length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 mb-4 text-xs text-zinc-500">
              <MapPin className="w-3.5 h-3.5 text-[#EA7B2B]" /> Zone:
              {status.last_run.zones.map((z) => (
                <span key={z} className="text-[11px] text-[#EA7B2B] border border-[#EA7B2B]/30 bg-[#EA7B2B]/10 px-2 py-0.5 rounded-full">{z}</span>
              ))}
            </div>
          )}
          <div className="grid grid-cols-5 gap-3 mb-4">
            <StatChip label="Trovate" value={status.last_run.found} />
            <StatChip label="Create" value={status.last_run.created} color="#10B981" />
            <StatChip label="Arricchite" value={status.last_run.enriched ?? 0} color="#8B5CF6" />
            <StatChip label="Già presenti" value={status.last_run.skipped} color="#71717A" />
            <StatChip label="Errori" value={status.last_run.errors} color={status.last_run.errors ? "#E11D48" : "#71717A"} />
          </div>
          <div className="bg-[#0A1826] border border-white/10 rounded-sm p-3 max-h-56 overflow-y-auto font-mono text-xs space-y-1" data-testid="run-logs">
            {(status.last_run.logs || []).map((l, i) => (
              <div key={i} className={l.includes("✓") ? "text-[#10B981]" : l.includes("⚠") || l.includes("✗") ? "text-[#D97706]" : "text-zinc-400"}>{l}</div>
            ))}
          </div>
        </div>
      )}

      {/* History */}
      <div className="bg-[#122A3F] border border-white/10 rounded-sm overflow-hidden" data-testid="runs-history">
        <div className="px-5 py-3 border-b border-white/10"><h2 className="text-sm font-medium text-zinc-200 uppercase tracking-wider">Storico esecuzioni</h2></div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-zinc-500 uppercase tracking-wider border-b border-white/10">
              <th className="px-5 py-2 font-medium">Data</th>
              <th className="px-5 py-2 font-medium">Fonte</th>
              <th className="px-5 py-2 font-medium">Stato</th>
              <th className="px-5 py-2 font-medium text-right">Trovate</th>
              <th className="px-5 py-2 font-medium text-right">Create</th>
              <th className="px-5 py-2 font-medium text-right">Errori</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((r) => (
              <tr key={r.id} className="border-b border-white/5">
                <td className="px-5 py-2 font-mono text-xs text-zinc-400">{r.started_at ? new Date(r.started_at).toLocaleString("it-IT") : "—"}</td>
                <td className="px-5 py-2 text-zinc-300">{(status.sources.find((s) => s.key === r.source) || {}).label || r.source || "—"}</td>
                <td className="px-5 py-2"><span className={r.status === "completed" ? "text-[#10B981]" : "text-[#D97706]"}>{r.status}</span></td>
                <td className="px-5 py-2 text-right font-mono text-zinc-400">{r.found}</td>
                <td className="px-5 py-2 text-right font-mono text-[#10B981]">{r.created}</td>
                <td className="px-5 py-2 text-right font-mono text-zinc-400">{r.errors}</td>
              </tr>
            ))}
            {runs.length === 0 && <tr><td colSpan={6} className="px-5 py-8 text-center text-zinc-500">Nessuna esecuzione registrata</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
